<script setup lang="ts">
  import { computed, inject, onScopeDispose, ref } from "vue";
  import type { HTMLAttributes } from "vue";
  import { useVModel } from "@vueuse/core";
  import { cn } from "@/lib/utils";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { RichInput, type MentionItem } from "@/components/episto-ui/rich-input";
  import AiButton from "./AiButton.vue";
  import AiReviewArea from "./AiReviewArea.vue";
  import { useAiTextInput } from "./useAiTextInput";
  import { AiCardReviewKey } from "./useAiCardReview";
  import type { AiGenerator } from "./types";

  const props = withDefaults(
    defineProps<{
      modelValue?: string;
      type?: "input" | "textarea" | "rich";
      rows?: number;
      placeholder?: string;
      mentionOptions?: MentionItem[];
      disabled?: boolean;
      generate?: AiGenerator;
      aiGroup?: "list";
      size?: "default" | "lg";
      class?: HTMLAttributes["class"];
      autofocus?: boolean;
    }>(),
    {
      modelValue: "",
      type: "input",
      rows: 2,
      placeholder: "",
      mentionOptions: () => [],
      disabled: false,
      generate: undefined,
      aiGroup: undefined,
      size: "default",
      class: undefined,
      autofocus: false,
    },
  );

  const emit = defineEmits<{ "update:modelValue": [value: string] }>();

  const localValue = useVModel(props, "modelValue", emit, { passive: true });

  const {
    mode,
    readonly: ownReadonly,
    displayedValue: ownDisplayedValue,
    showOriginal,
    onSelect,
    retry,
    accept,
    cancel,
    toggleOriginal,
  } = useAiTextInput({ value: localValue, generate: props.generate });

  const cardReview = inject(AiCardReviewKey, null);
  const cardField = cardReview?.register({
    getValue: () => localValue.value ?? "",
    setValue: (value) => (localValue.value = value),
    group: props.aiGroup,
    isDisabled: () => props.disabled,
  });
  if (cardField) onScopeDispose(() => cardField.unregister());

  const cardChanged = computed(() => (cardReview && cardField ? cardReview.isChanged(cardField.key) : false));
  const readonly = computed(() => ownReadonly.value || (cardReview?.readonly.value ?? false));
  const displayedValue = computed(() => {
    const cardValue = cardReview && cardField ? cardReview.displayedValue(cardField.key) : null;
    return cardValue ?? ownDisplayedValue.value;
  });

  const hovered = ref(false);
  const pickerOpen = ref(false);
  const emojiOpen = ref(false);

  const generating = computed(() => mode.value === "generating");
  const reviewing = computed(() => mode.value === "reviewing");
  const empty = computed(() => !localValue.value || localValue.value.trim() === "");
  const highlighted = computed(() => reviewing.value || cardChanged.value);
  const iconVisible = computed(
    () => !props.disabled && (hovered.value || pickerOpen.value || emojiOpen.value || mode.value !== "idle"),
  );

  const onInput = (value: string | number): void => {
    if (!readonly.value) localValue.value = String(value);
  };
</script>

<template>
  <div
    :class="cn('w-full', props.class)"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false">
    <div class="relative w-full">
      <RichInput
        v-if="type === 'rich'"
        :key="placeholder"
        :value="displayedValue"
        :type="rows > 1 ? 'textarea' : 'input'"
        :rows="rows"
        :mention-options="mentionOptions"
        :controls-active="iconVisible"
        :emoji-open="emojiOpen"
        :readonly="readonly"
        :disabled="disabled"
        :placeholder="placeholder"
        :size="size"
        :autofocus="autofocus"
        :class="cn(highlighted && 'border-primary')"
        @update-input="onInput"
        @update:emoji-open="emojiOpen = $event">
        <template
          v-if="!cardReview"
          #append>
          <div v-show="iconVisible">
            <AiButton
              v-model:open="pickerOpen"
              :disabled="generating"
              :busy="generating"
              :empty="empty"
              @select="onSelect" />
          </div>
        </template>
      </RichInput>
      <component
        :is="type === 'textarea' ? Textarea : Input"
        v-else
        :model-value="displayedValue"
        :rows="type === 'textarea' ? rows : undefined"
        :readonly="readonly"
        :disabled="disabled"
        :placeholder="placeholder"
        :class="cn(highlighted && 'border-primary', !cardReview && 'pr-8')"
        @update:model-value="onInput" />
      <div
        v-if="type !== 'rich' && !cardReview"
        v-show="iconVisible"
        class="absolute right-1.5 top-1.5 z-10">
        <AiButton
          v-model:open="pickerOpen"
          :disabled="generating"
          :busy="generating"
          :empty="empty"
          @select="onSelect" />
      </div>
    </div>

    <AiReviewArea
      class="mt-2"
      :mode="mode"
      :show-original="showOriginal"
      @accept="accept"
      @toggle="toggleOriginal"
      @retry="retry"
      @cancel="cancel" />
  </div>
</template>
